import ReactGA from 'react-ga4';
import Fingerprint2 from 'fingerprintjs2';
import {
  ANALYTICS_API_DELAY_TIME,
  ANALYTICS_EVENTS,
  JJ_LOCAL_STORAGE,
  JJ_SESSION_STORAGE,
  ROUTES,
  STOCK_CHECK,
  TRACKING_FIELDS,
} from '../constants/constants';
import analyticsApi from '../api/analytics.api';
import {getApiConfig} from '../config/configProvider';
import {getJJDeviceIdCookieValue} from './data.helper';
import {
  getDataFromSessionStorage,
  persistDataToSessionStorage,
  removeValueFromSessionStorage,
} from './sessionStorage.helper';
import {v4 as uuidv4} from 'uuid';

// Keep only scalar fields from a redux action when tracking it generically —
// nested objects/arrays (e.g. full product lists) blow past GA4's param value limits.
export const summarizeAction = action => {
  const summary = {};
  Object.keys(action || {}).forEach(key => {
    if (key === 'type') return;
    const value = action[key];
    if (value === null || typeof value !== 'object') {
      summary[key] = value;
    }
  });
  return summary;
};

export class GoogleAnalyticsHelper {
  static FULLFILLMENT_DIMENSION = null;
  static USER_INFO = {};
  static INITIALIZED = false;
  static DEVICE_ID = null;
  static STORE = {};
  static writeLock = Promise.resolve();

  static initialize() {
    const config = getApiConfig();
    if (!config || !config.googleAnalytics || !config.googleAnalytics.enabled) {
      return;
    }

    this.DEVICE_ID = getJJDeviceIdCookieValue();
    this.USER_INFO.sessionId = getDataFromSessionStorage(
      JJ_SESSION_STORAGE.SESSION_ID
    );

    if (window && !this.DEVICE_ID) {
      Fingerprint2.get(components => {
        const values = components.map(component => component.value);
        const generatedDeviceId = Fingerprint2.x64hash128(values.join(''), 31);
        this.DEVICE_ID = generatedDeviceId;
        this.USER_INFO.deviceHash = this.DEVICE_ID;
        document.cookie = JJ_LOCAL_STORAGE.DEVICE_ID + '=' + generatedDeviceId;
      });
    } else {
      this.USER_INFO.deviceHash = this.DEVICE_ID;
    }

    if (window && !this.USER_INFO.sessionId) {
      const sessionId = uuidv4();
      persistDataToSessionStorage(JJ_SESSION_STORAGE.SESSION_ID, sessionId);
      this.USER_INFO.sessionId = sessionId;
    }

    if (
      typeof window !== 'undefined' &&
      window.location &&
      !config.googleAnalytics.debug
    ) {
      this.USER_INFO.domain = 'https://' + window.location.hostname;
    }
    if (typeof navigator !== 'undefined') {
      // navigator.platform is deprecated but still widely supported and is
      // the closest browser-native equivalent to APP's Device.modelId /
      // KIOSK's platform label — WEB never set this field at all before.
      this.USER_INFO.platform = navigator.platform || navigator.userAgent;
    }
    this.INITIALIZED = true;

    if (config.googleAnalytics.trackingID) {
      ReactGA.initialize(config.googleAnalytics.trackingID, {
        gtagOptions: {send_page_view: false},
      });
    }
  }

  static updateStore = store => {
    this.STORE = Object.assign({}, this.STORE, store);
  };

  static clearUserInfo() {
    // USER_INFO only ever holds device/session baseline fields (sessionId,
    // deviceHash, domain, language, platform — set once by initialize());
    // customer-identity fields (axBasketId, branch, c_account, l_account,
    // customerType, UTM, etc.) live entirely in the sessionStorage tracking
    // blob. This used to instead wipe USER_INFO (`this.USER_INFO = {}`),
    // destroying the baseline fields with nothing to re-populate them until a
    // full page reload — every event fired after a logout got an empty
    // userInfo (no source/platform/sessionId at all), confirmed via live
    // cross-platform testing on 2026-09-02 (same bug found on KIOSK/APP).
    // KIOSK/APP's clearUserInfo() already clears this same tracking blob on
    // logout for the equivalent reason (stale identity leaking into the next
    // session); WEB never did — bringing it in line here.
    removeValueFromSessionStorage(JJ_LOCAL_STORAGE.TRACKING_KEY);
  }
  static clearSession() {
    const sessionId = uuidv4();
    persistDataToSessionStorage(JJ_SESSION_STORAGE.SESSION_ID, sessionId);
    this.USER_INFO.sessionId = sessionId;
    removeValueFromSessionStorage(JJ_LOCAL_STORAGE.TRACKING_KEY);
  }

  static setFulfillment(type) {
    if (!this.INITIALIZED) {
      return;
    }
    if (type !== this.FULLFILLMENT_DIMENSION) {
      this.FULLFILLMENT_DIMENSION = type;
    }
  }

  /**
   * Sets the current search-attribution context. Read by addProduct,
   * removeProduct and logPageView when they fire. Pass null to clear.
   * Each event-firing site should set this immediately before the event —
   * stale context leaks into unrelated events otherwise.
   */
  static setSearchContext(context) {
    this.SEARCH_CONTEXT = context || null;
  }

  /**
   * Method that logs on Google Analytics the current page.
   * @returns void
   */
  static logPageView(url, time) {
    const config = getApiConfig();
    if (
      !config ||
      !config.googleAnalytics ||
      !config.googleAnalytics.enabled ||
      !this.INITIALIZED
    ) {
      return;
    }
    const timeStamp = time || new Date();
    const location =
      url ||
      (typeof window !== 'undefined' &&
        window.location &&
        window.location.pathname);

    const title =
      url ||
      (typeof window !== 'undefined' &&
        window.document &&
        window.document.title);

    const referrer =
      url || (typeof window !== 'undefined' && window.document.referrer);

    if (!location || !title) {
      return;
    }

    if (this.DEVICE_ID) {
      const ctx = this.SEARCH_CONTEXT;
      this.trackEvent(ANALYTICS_EVENTS.PAGE_VIEW, {
        page_location: location,
        page_referrer: referrer,
        page_title: title,
        ...(ctx
          ? {searchId: ctx.searchId, searchPosition: ctx.searchPosition}
          : {}),
      });
      const isDelivery = this.FULLFILLMENT_DIMENSION === 'DELIVERY';
      if (location.indexOf('/' + ROUTES.BASKET) > 1) {
        const items = this.STORE.basket.items.map(product =>
          this.simplifyProduct(product, product.quantity, isDelivery)
        );
        const value = this.getTotalValue(items);
        this.trackEvent(ANALYTICS_EVENTS.VIEW_CART, {
          currency: 'GBP',
          value,
          items,
        });
      } else if (location.indexOf('/' + ROUTES.PRODUCT) > 1) {
        const item = this.simplifyProduct(
          this.STORE.productDetail,
          undefined,
          isDelivery
        );
        const value = this.getTotalValue([item]);
        this.trackEvent(ANALYTICS_EVENTS.VIEW_ITEM, {
          currency: 'GBP',
          value,
          items: [item].filter(Boolean),
        });
      }
    } else {
      const retryTimer = setTimeout(() => {
        this.logPageView(location, timeStamp);
      }, ANALYTICS_API_DELAY_TIME);
    }
  }

  static simplifyProduct(product, quantity, isDelivery) {
    if (!product) {
      return;
    }

    const price =
      (isDelivery
        ? product.delivery?.price ?? product.price
        : product.collection?.price ?? product.price) ?? product.priceIncVat;

    const namePrefix =
      product.stockCheck &&
      product.stockCheck.result === STOCK_CHECK.OUT_OF_STOCK
        ? 'Out of stock - '
        : '';
    const productName =
      product.itemName ||
      product.item_name ||
      (product.product && product.product.itemName) ||
      product.itemId;

    return {
      item_id: product.itemId,
      item_name: namePrefix + productName,
      item_category: (
        product.categoryName ||
        product.category1Name ||
        product.item_category ||
        (product.product && product.product.categoryName) ||
        ''
      ).replace(',', '/'),
      price,
      quantity: quantity || product.quantity,
      currency: 'GBP',
    };
  }

  static getTotalValue = simpleItems => {
    if (!simpleItems || !Array.isArray(simpleItems)) {
      return 0;
    }
    let value = 0;
    simpleItems.map(item => {
      if (item && item.price) {
        value += item.price * (item.quantity || 1);
      }
      return item;
    });
    return value;
  };

  static trackEvent(event, data) {
    if (!this.INITIALIZED) {
      return;
    }
    const clientSession = getDataFromSessionStorage(
      JJ_LOCAL_STORAGE.TRACKING_KEY
    );
    // Copy rather than mutate: callers pass live objects (e.g. the search
    // page's URL params), and writing userInfo onto them leaked
    // `userInfo=[object Object]` into the search URL.
    const payload = data
      ? {...data, userInfo: {...this.USER_INFO, ...clientSession}}
      : data;
    const config = getApiConfig();
    if (config.googleAnalytics && config.googleAnalytics.debug) {
      console.log('[Joacalytics]', event, payload); // eslint-disable-line
    }
    const timeStamp = new Date();
    analyticsApi.trackEvent(event, payload, timeStamp);
  }

  static getAnalyticsTopBannerEventName(index) {
    return `home_sliding_banner_${index + 1}`;
  }

  static getAnalyticsMarketingGridEventName(index, isMain) {
    return isMain ? `home_large_block_${index}` : `home_small_block_${index}`;
  }

  static async setTrackingFlag(flags) {
    if (typeof flags !== 'object' || flags === null) return;
    await this.setTrackingContext(flags);
  }

  static async setTrackingContext(data = {}) {
    this.writeLock = this.writeLock.then(async () => {
      const urlParams = new URLSearchParams(window.location.search);
      const keys = [
        'gclid',
        'utm_source',
        'utm_medium',
        'utm_campaign',
        'utm_term',
        'utm_content',
        'utm_id',
        'fbclid',
        TRACKING_FIELDS.SRSLTID,
      ];

      const paramData = {};
      for (const key of keys) {
        const val = urlParams.get(key);
        if (val) {
          paramData[key] = val;
        }
      }

      if (typeof document !== 'undefined' && document.referrer) {
        paramData[TRACKING_FIELDS.LANDING_REFERRER] = document.referrer;
      }

      const existing = getDataFromSessionStorage(JJ_LOCAL_STORAGE.TRACKING_KEY);
      const merged = {...existing, ...paramData, ...data};
      persistDataToSessionStorage(JJ_LOCAL_STORAGE.TRACKING_KEY, merged);
    });

    return this.writeLock;
  }
}
